import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../services/firebase";

const MEL_TZ = "Australia/Melbourne";

function todayInMelbourne() {
  return new Date().toLocaleDateString("en-CA", { timeZone: MEL_TZ });
}

function monthKeyFromDate(dateStr) {
  return String(dateStr || todayInMelbourne()).slice(0, 7);
}

function getMonthLabel(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-AU", {
    month: "long",
    year: "numeric",
  });
}

function getMonthRange(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  const start = `${monthKey}-01`;
  const end = new Date(year, month, 0).toLocaleDateString("en-CA", {
    timeZone: MEL_TZ,
  });
  return { start, end };
}

function buildCalendarCells(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  const first = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const startWeekday = (first.getDay() + 6) % 7;

  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(`${monthKey}-${String(day).padStart(2, "0")}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function getJobIdentity(job, index = 0, runLabel = "") {
  if (job == null) return "";

  if (typeof job === "string" || typeof job === "number") {
    return String(job);
  }

  if (typeof job !== "object") {
    return `${runLabel}_idx_${index}`;
  }

  return (
    job.jobId ||
    job.id ||
    job.stopId ||
    job.orderId ||
    job.docketNo ||
    [
      job.customerName || job.companyName || "",
      job.siteName || job.siteLabel || job.customerSite || "",
      job.address || job.siteAddress || job.location || "",
      job.jobTypeName || job.jobType || job.cardType || job.type || "",
      job.binType || job.binName || "",
      job.qty || job.quantity || job.binCount || "",
      index,
    ].join("|")
  );
}

function getRunJobs(runValue) {
  if (!runValue) return [];
  if (Array.isArray(runValue)) return runValue.filter(Boolean);

  const jobsField =
    runValue.jobs || runValue.jobIds || runValue.jobList || runValue.jobIDs;

  if (Array.isArray(jobsField)) return jobsField.filter(Boolean);
  if (jobsField && typeof jobsField === "object") {
    return Object.entries(jobsField).map(([key, value]) => {
      if (value && typeof value === "object") {
        return value;
      }
      return { id: key, value };
    });
  }

  return [];
}

function getRunCompanyName(label, runValue, runMeta) {
  const meta = runMeta?.[label] || {};
  const firstJob = Array.isArray(runValue) ? runValue[0] : null;

  return (
    meta.companyName ||
    firstJob?.companyName ||
    (meta.companyId && meta.companyId !== "mixed" ? meta.companyId : "") ||
    ""
  );
}

function isDocForDriver(docId, data, uid) {
  if (!uid) return false;
  if (data?.driverId === uid) return true;
  if (docId === `${uid}_${data?.date || ""}`) return true;
  if (docId.startsWith(`${uid}_`)) return true;
  if (docId.includes(`_${uid}_`)) return true;
  if (docId.endsWith(`_${uid}`)) return true;
  return false;
}

function summariseDocsByDate(docs) {
  const byDate = {};

  for (const item of docs) {
    const date = item?.date;
    if (!date) continue;

    if (!byDate[date]) {
      byDate[date] = {
        date,
        docIds: [],
        totalJobs: 0,
        runsMap: {},
        dayJobKeySet: new Set(),
      };
    }

    const bucket = byDate[date];
    bucket.docIds.push(item.id);

    const runs = item?.runs || {};
    const runMeta = item?.runMeta || {};

    Object.entries(runs).forEach(([label, runValue]) => {
      const runJobs = getRunJobs(runValue);
      if (!runJobs.length) return;

      if (!bucket.runsMap[label]) {
        bucket.runsMap[label] = {
          label,
          count: 0,
          companyName: getRunCompanyName(label, runValue, runMeta),
          jobKeySet: new Set(),
        };
      }

      const runBucket = bucket.runsMap[label];
      if (!runBucket.companyName) {
        runBucket.companyName = getRunCompanyName(label, runValue, runMeta);
      }

      runJobs.forEach((job, index) => {
        const jobKey = getJobIdentity(job, index, label);
        if (!jobKey) return;
        runBucket.jobKeySet.add(jobKey);
        bucket.dayJobKeySet.add(jobKey);
      });
    });
  }

  Object.values(byDate).forEach((bucket) => {
    bucket.runs = Object.values(bucket.runsMap)
      .map((run) => ({
        ...run,
        count: run.jobKeySet.size,
      }))
      .filter((run) => run.count > 0)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    bucket.totalRuns = bucket.runs.length;
    bucket.totalJobs = bucket.dayJobKeySet.size;
    bucket.runs.forEach((run) => delete run.jobKeySet);
    delete bucket.dayJobKeySet;
    delete bucket.runsMap;
  });

  return byDate;
}

export default function DriverCalendar() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [authUid, setAuthUid] = useState(() => auth.currentUser?.uid || "");
  const [monthKey, setMonthKey] = useState(() => monthKeyFromDate(todayInMelbourne()));
  const [loading, setLoading] = useState(true);
  const [assignmentsByDate, setAssignmentsByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(todayInMelbourne());

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthUid(user?.uid || "");
    });
    return () => unsub();
  }, [auth]);

  useEffect(() => {
    if (!authUid) {
      setAssignmentsByDate({});
      setLoading(false);
      return;
    }

    let active = true;
    const { start, end } = getMonthRange(monthKey);

    setLoading(true);

    (async () => {
      try {
        const monthQuery = query(
          collection(db, "assignments"),
          where("date", ">=", start),
          where("date", "<=", end),
        );

        const snap = await getDocs(monthQuery);
        if (!active) return;

        const raw = snap.docs
          .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }))
          .filter((item) => isDocForDriver(item.id, item, authUid));

        const next = summariseDocsByDate(raw);
        setAssignmentsByDate(next);

        const visibleDates = Object.keys(next).sort();
        const selectedStillValid = !!next[selectedDate];
        const isSelectedInCurrentMonth = monthKeyFromDate(selectedDate) === monthKey;

        if (!selectedStillValid) {
          if (visibleDates.length) {
            setSelectedDate(visibleDates[0]);
          } else if (!isSelectedInCurrentMonth) {
            setSelectedDate(`${monthKey}-01`);
          }
        }
      } catch (error) {
        console.error("Failed to load driver calendar:", error);
        if (active) setAssignmentsByDate({});
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [authUid, monthKey, selectedDate]);

  const cells = useMemo(() => buildCalendarCells(monthKey), [monthKey]);
  const selectedAssignment = assignmentsByDate[selectedDate] || null;
  const selectedRuns = selectedAssignment?.runs || [];
  const totalAssignedDays = Object.keys(assignmentsByDate).length;
  const totalAssignedJobs = useMemo(
    () => Object.values(assignmentsByDate).reduce((sum, item) => sum + (item.totalJobs || 0), 0),
    [assignmentsByDate],
  );

  const monthOptions = useMemo(() => {
    const current = new Date(`${monthKey}-01T00:00:00`);
    return [-1, 0, 1, 2].map((offset) => {
      const d = new Date(current.getFullYear(), current.getMonth() + offset, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      return { key, label: getMonthLabel(key) };
    });
  }, [monthKey]);

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-4 sm:space-y-6 text-gray-900">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <select
          value={monthKey}
          onChange={(e) => setMonthKey(e.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm shadow-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100"
        >
          {monthOptions.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          onClick={() => navigate(`/dashboard?date=${selectedDate}`)}
          disabled={!selectedAssignment}
          className={`rounded-xl px-3.5 py-2.5 text-sm font-medium text-white shadow-sm transition ${
            selectedAssignment ? "bg-sky-600 hover:bg-sky-700" : "cursor-not-allowed bg-gray-300"
          }`}
        >
          Open selected day
        </button>
        <div className="ml-auto text-xs text-gray-500">
          <span className="font-semibold text-gray-900">{totalAssignedDays}</span> assigned days
          {" · "}
          <span className="font-semibold text-gray-900">{totalAssignedJobs}</span> jobs this month
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_380px]">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
              <div key={day} className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {cells.map((date, index) => {
              const info = date ? assignmentsByDate[date] : null;
              const isSelected = !!date && selectedDate === date;
              const isToday = date === todayInMelbourne();

              return (
                <button
                  key={`${date || "empty"}-${index}`}
                  type="button"
                  disabled={!date}
                  onClick={() => date && setSelectedDate(date)}
                  className={[
                    "min-h-[105px] border-r border-b border-gray-200 p-3 text-left align-top transition",
                    date ? "hover:bg-sky-50" : "bg-gray-50/50 cursor-default",
                    isSelected ? "bg-sky-50" : "bg-white",
                  ].join(" ")}
                >
                  {date ? (
                    <div className="flex h-full flex-col gap-2">
                      <div className="flex items-start justify-between">
                        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                          isToday ? "bg-gray-950 text-white" : "text-gray-900"
                        }`}>
                          {Number(date.slice(-2))}
                        </span>
                        {info?.totalRuns ? (
                          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                            {info.totalRuns} run{info.totalRuns !== 1 ? "s" : ""}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-auto space-y-1">
                        {info?.runs?.length ? (
                          <>
                            <div className="text-xs font-medium text-gray-700">
                              {info.totalJobs} job{info.totalJobs !== 1 ? "s" : ""}
                            </div>
                            <div className="space-y-1">
                              {info.runs.slice(0, 2).map((run) => (
                                <div key={run.label} className="truncate text-[11px] text-gray-500">
                                  {run.label} · {run.count}
                                </div>
                              ))}
                              {info.runs.length > 2 ? (
                                <div className="text-[11px] text-gray-400">+{info.runs.length - 2} more</div>
                              ) : null}
                            </div>
                          </>
                        ) : (
                          <div className="text-xs text-gray-400">No runs</div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-950">Selected day</h2>
              <p className="mt-1 text-sm text-gray-500">{selectedDate}</p>
            </div>
            <button
              onClick={() => navigate(`/dashboard?date=${selectedDate}`)}
              disabled={!selectedAssignment}
              className={`rounded-xl px-3 py-2 text-sm font-medium transition ${
                selectedAssignment
                  ? "border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
                  : "cursor-not-allowed border border-gray-200 bg-gray-50 text-gray-400"
              }`}
            >
              Go to runs
            </button>
          </div>

          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
                Loading calendar...
              </div>
            ) : !selectedAssignment ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
                No assigned runs for this date.
              </div>
            ) : (
              selectedRuns.map((run) => (
                <div key={run.label} className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{run.label}</div>
                      {run.companyName ? (
                        <div className="mt-1 text-xs text-gray-500">{run.companyName}</div>
                      ) : null}
                    </div>
                    <div className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 shadow-sm border border-gray-200">
                      {run.count} job{run.count !== 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
